"use client";

import { motion } from "framer-motion";
import { Products } from "src/types/productsTypes";

export default function Benefits({ section }: { section: Products['atlaside']['benefits'] }) {
    return (
        <section className="section-primary">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-block w-fit mx-auto badge-size section-badge mb-1"
                    >
                        {section.badge}
                    </motion.span>
                    
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="title-size font-bold mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent"
                    >
                        {section.title}
                    </motion.h2>
                    
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-size text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        {section.description}
                    </motion.p>
                </div>

                <div className="space-y-12">
                    {section.cards.map((benefit, index) => (
                        <motion.div
                            key={benefit.title}
                            initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            className="group"
                        >
                            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 relative">
                                <div className="relative z-10">
                                    <div className="flex items-start gap-6">
                                        {/* Number indicator */}
                                        <div className="flex-shrink-0 absolute top-0 left-0 -translate-x-1/2 -translate-y-[100%]">
                                            <div className="size-10 md:size-12 iconBudg-v2 flex items-center justify-center text-white font-bold text-base md:text-lg shadow-lg">
                                                {index + 1}
                                            </div>
                                        </div>

                                        <div className="flex-1">
                                            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                                                {benefit.title.split(' ').map((word, i) =>
                                                    word === benefit.highlight ? 
                                                        <span key={i} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{word} </span> : 
                                                        <span key={i}>{word} </span>
                                                )}
                                            </h3>
                                            <p className="text-gray-600 leading-relaxed text-sm md:text-lg">
                                                {benefit.description}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="text-center mt-12"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-xs md:text-sm font-medium text-gray-700">
                            {section.cta}
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}